import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, type ReactElement, cloneElement, useCallback, useMemo } from 'react';
// @ts-expect-error RHF v6 — FormBuilder uses legacy form API; v7 lives in `react-hook-form-latest` elsewhere
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import { _isNotEmpty, _omit } from '@/littledash';

import { FormBuilderItem, Header } from '@/components/FormBuilder';
import type {
  FormBuilderFieldOption,
  FormBuilderFormData,
  FormBuilderFormItem,
} from '@/components/FormBuilder/FormBuilder.model';
import { ErrorTypes, nonFormFieldTypes } from '@/components/FormBuilder/FormBuilder.utils';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';

export type {
  FormBuilderFieldOption,
  FormBuilderFormData,
  FormBuilderFormItem,
} from '@/components/FormBuilder/FormBuilder.model';

interface FormBuilderColumn {
  id: string;
  title: string;
  width: number;
}

interface FormBuilderProps {
  ActionButtons: ReactElement<{ onOk?: () => void | Promise<void> }>;
  onSubmit: (formItems: Array<FormBuilderFormItem>) => void;
  defaultValue?: Array<FormBuilderFormItem>;
}

const columns: Array<FormBuilderColumn> = [
  {
    id: 'fieldType',
    title: 'Field Type',
    width: 200,
  },
  {
    id: 'fieldProperties',
    title: 'Field Properties',
    width: 500,
  },
];

const defaultOption: FormBuilderFieldOption = {
  label: '',
  value: '',
};

const FormBuilder: FC<FormBuilderProps> = ({ ActionButtons, onSubmit, defaultValue }) => {
  const formMethods = useForm<FormBuilderFormData>({
    defaultValues: {
      formItems: defaultValue ?? [
        {
          label: '',
          type: '',
          options: [defaultOption],
          required: undefined,
        },
      ],
    },
  });

  const { control, handleSubmit, setError, clearErrors, errors, getValues } = formMethods;

  const {
    fields: formItems,
    append: appendToFormItems,
    remove: removeFromFormItems,
    move,
  } = useFieldArray({
    control,
    name: 'formItems',
  });

  const handleRemoveFormItemClick = useCallback(
    (index: number) => {
      clearErrors('formItems');
      removeFromFormItems(index);
    },
    [clearErrors, removeFromFormItems]
  );

  const handleAddFormItemClick = useCallback(() => {
    clearErrors('formItems');
    appendToFormItems({
      label: '',
      type: '',
      options: [defaultOption],
    });
  }, [appendToFormItems, clearErrors]);

  const validateFormItems = useCallback((items: Array<FormBuilderFormItem>) => {
    const isFormField = (formItem: FormBuilderFormItem) => !nonFormFieldTypes.includes(formItem.type);
    return items.some(isFormField);
  }, []);

  const handleFieldTypeChange = useCallback(() => {
    clearErrors('formItems');
  }, [clearErrors]);

  const handleReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      move(sourceIndex, destinationIndex);
    },
    [move]
  );

  const renderItem = useMemo(
    () =>
      asSortableListItem({
        canSort: formItems.length > 1,
        itemClassName: 'items-center bb b--moon-gray pv3 ph3',
        rowRemove: {
          enabled: true,
          visible: formItems.length > 1,
          onRemoveClick: handleRemoveFormItemClick,
        },
      })(FormBuilderItem, {
        columns,
        onFieldTypeChange: handleFieldTypeChange,
      }),
    [formItems.length, handleRemoveFormItemClick, handleFieldTypeChange]
  );

  return (
    <FormProvider {...formMethods}>
      <div className="ba br1" data-test-component="FormBuilder" data-test-element="container">
        <Header labels={columns} />
        <DraggableList
          canAmend
          canAdd
          canRemove={formItems.length > 1}
          data={formItems}
          sortable={{
            droppableId: 'form-item',
            onReorder: handleReorder,
            disabled: formItems.length <= 1,
          }}
          renderItem={renderItem}
          itemProps={{
            className: 'items-center',
          }}
          onAddClick={handleAddFormItemClick}
          onRemoveClick={handleRemoveFormItemClick}
          addButtonText="Add field"
          containerProps={{
            className: 'bg-white br3 br--bottom pb3 pt3',
          }}
          addButtonProps={{
            className: 'mb1 ml3',
          }}
        />
        <ErrorMessage
          errors={errors}
          name="formItems"
          render={({ message }: { message: string }) => <p className={cn('bg-white pa3 f6 red db pt2')}>{message}</p>}
        />
      </div>
      {cloneElement(ActionButtons, {
        // Prefer `data.formItems` from `handleSubmit`; when missing (e.g. mocked `FormBuilderItem` without full RHF registration), merge field-array rows with per-index `getValues`.
        onOk: handleSubmit((data: FormBuilderFormData) => {
          let rows: Array<FormBuilderFormItem>;
          if (_isNotEmpty(data.formItems)) {
            rows = data.formItems.map((row: FormBuilderFormItem & { id?: string }) => {
              return _omit(row, ['id']) as unknown as FormBuilderFormItem;
            });
          } else {
            rows = formItems.map((field: FormBuilderFormItem & { id: string }, index: number) => {
              const base = _omit(field, ['id']) as unknown as FormBuilderFormItem;
              const live = getValues(`formItems.${index}`) as Partial<FormBuilderFormItem> | undefined;
              return { ...base, ...(live ?? {}) } as FormBuilderFormItem;
            });
          }
          const cleanedRows = rows.map((row) => ({
            ...row,
            options: _isNotEmpty(row.options)
              ? row.options.map<FormBuilderFieldOption>(({ clientRowId, ...rest }) => rest)
              : row.options,
          }));
          if (!validateFormItems(cleanedRows)) {
            setError('formItems', {
              type: ErrorTypes.formFieldRequired,
              message: 'At least one row must be a form element.',
            });
            return;
          }
          onSubmit(cleanedRows);
        }),
      })}
    </FormProvider>
  );
};

export default FormBuilder;
